<template>
  <div class="share-page">
    <header class="share-header">
      <div class="share-container share-header-inner">
        <div class="student-summary">
          <div>
            <div class="student-name">姓名：{{ studentName }}</div>
            <div class="student-meta" v-if="studentId">考生ID：{{ studentId }}</div>
          </div>
          <img class="student-logo" src="@/assets/images/logo.png" alt="" />
        </div>
        <div class="exam-time">进入考试时间：{{ examInfo.enroll_start_time || '-' }}</div>
        <div class="exam-time">交卷时间：{{ examInfo.enroll_end_time || '-' }}</div>
      </div>
    </header>

    <main class="share-container share-main">
      <button class="card-toggle" type="button" @click="navOpen = !navOpen">
        <span>答题卡</span>
        <span class="toggle-icon">{{ navOpen ? '−' : '+' }}</span>
      </button>

      <aside class="answer-card-panel" v-show="!isMobile || navOpen">
        <div class="panel-title">答题卡</div>
        <div v-if="answerGroups.length" class="answer-groups">
          <section class="answer-group" v-for="group in answerGroups" :key="group.id">
            <h3>{{ group.name }}</h3>
            <div class="answer-child" v-for="child in group.children" :key="child.id">
              <div class="child-title">{{ child.name }}</div>
              <div class="answer-numbers">
                <button
                  v-for="part in child.parts"
                  :key="part.key"
                  type="button"
                  class="answer-number"
                  :class="{ active: part.index === currentIndex }"
                  @click="selectPart(part.index)"
                >
                  {{ part.label }}
                </button>
              </div>
            </div>
          </section>
        </div>
        <div v-else class="panel-empty">暂无答题卡</div>
      </aside>

      <section class="question-panel">
        <div class="question-heading">
          <div class="question-title">
            <span>{{ currentPart.name || currentPart.title || '试题解析' }}</span>
          </div>
          <button class="desktop-card-button" type="button" @click="navOpen = !navOpen">
            {{ navOpen ? '收起答题卡' : '打开答题卡' }}
          </button>
        </div>

        <div v-if="loading" class="state-box">正在加载试卷...</div>
        <template v-else>
          <div v-if="answerInfo.length" class="answer-table-wrap">
            <table class="answer-table">
              <thead>
                <tr>
                  <th>题号</th>
                  <th>本题得分/分数</th>
                  <th>学生答案</th>
                  <th>正确答案</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in answerInfo" :key="row.id || row.question_id">
                  <td>{{ row.question_id || '-' }}</td>
                  <td>
                    {{ row.get_score == null ? 0 : row.get_score }}/{{ row.score == null ? 0 : row.score }}
                    <span class="result-mark" :class="row.is_right == 2 ? 'correct' : 'wrong'">
                      {{ row.is_right == 2 ? '✓' : '×' }}
                    </span>
                  </td>
                  <td v-html="formatAnswer(row.student_answer, row.question)"></td>
                  <td v-html="formatAnswer(row.answer, row.question)"></td>
                </tr>
              </tbody>
            </table>
          </div>

          <article v-if="currentQuestion && currentQuestion.id" class="question-detail">
            <div class="question-meta">
              <span>ID：{{ currentQuestion.id }}</span>
              <span>题型：{{ currentQuestion.question_type && currentQuestion.question_type.name }}</span>
              <span>题库：{{ currentQuestion.storage_name || '-' }}</span>
            </div>

            <div v-if="questionSections.length" class="question-content">
              <section class="question-section" v-for="(section, index) in questionSections" :key="index">
                <div v-if="section.heading" class="question-heading-text" v-html="section.heading"></div>
                <div v-if="section.options && section.options.length" class="question-options">
                  <div class="question-option" v-for="(option, optionIndex) in section.options" :key="option.id || optionIndex">
                    <span class="option-key">{{ option.option_k || letter(optionIndex) }}</span>
                    <span class="option-content" v-html="option.option || option.text || option"></span>
                  </div>
                </div>
              </section>
            </div>
            <div v-else class="question-content" v-html="questionFallback"></div>

            <div v-if="analysisText" class="analysis-text">
              <strong>解析：</strong><span v-html="analysisText"></span>
            </div>
            <div v-if="currentQuestion.knowledge" class="knowledge-text">
              <strong>考点：</strong>{{ currentQuestion.knowledge }}
            </div>
          </article>
          <div v-else class="state-box">暂无题目数据</div>
        </template>

        <nav class="question-navigation">
          <button type="button" class="previous-button" :disabled="currentIndex <= 0" @click="selectPart(currentIndex - 1)">
            上一题
          </button>
          <span>{{ currentIndex + 1 }} / {{ flatParts.length || 0 }}</span>
          <button type="button" class="next-button" :disabled="currentIndex >= flatParts.length - 1" @click="selectPart(currentIndex + 1)">
            下一题
          </button>
        </nav>
      </section>
    </main>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
export default {
  name: "ExamShareDetail",
  data() {
    return {
      shareToken: "",
      examInfo: {},
      catalog: [],
      flatParts: [],
      answerInfo: [],
      currentQuestion: {},
      currentIndex: 0,
      navOpen: false,
      isMobile: false,
      loading: true,
    };
  },
  computed: {
    studentName() {
      const student = this.examInfo.student || {};
      return student.real_name || this.examInfo.real_name || "-";
    },
    studentId() {
      const student = this.examInfo.student || {};
      return student.name || this.examInfo.student_id || "";
    },
    currentPart() {
      return this.flatParts[this.currentIndex] || {};
    },
    answerGroups() {
      return (this.catalog || []).map((group, groupIndex) => ({
        id: group.id || `group-${groupIndex}`,
        name: group.name || group.title || `第${groupIndex + 1}部分`,
        children: (group.child || group.children || []).map((child, childIndex) => ({
          id: child.id || `child-${groupIndex}-${childIndex}`,
          name: child.name || child.title || "试题",
          parts: (child.parts || []).map((part, partIndex) => {
            const flatIndex = this.flatParts.indexOf(part);
            return {
              key: part.id || `${groupIndex}-${childIndex}-${partIndex}`,
              index: flatIndex,
              label: part.start || part.question_num || partIndex + 1,
            };
          }),
        })),
      }));
    },
    questionSections() {
      const question = this.currentQuestion || {};
      const format = question.formatData || {};
      const sections = [];
      const add = (heading, options) => {
        if (heading || (options && options.length)) {
          sections.push({ heading: heading || "", options: options || [] });
        }
      };

      add(format.stem_src_txt, []);
      add(format.stem, []);
      if (format.sub_heading) add(format.sub_heading, format.options || []);
      else if (format.options) add("", format.options);
      if (format.sub_headings) {
        format.sub_headings.forEach((item) => {
          add(item.sub_heading || item.sub_src_txt || "", item.options || []);
        });
      }
      if (!sections.length && question.stem) add(question.stem, []);
      return sections;
    },
    questionFallback() {
      const format = (this.currentQuestion || {}).formatData || {};
      return format.stem_src_txt || format.stem || format.sub_heading || "";
    },
    analysisText() {
      const analysis = this.currentQuestion && this.currentQuestion.ques_analysis;
      return analysis && analysis.length ? analysis[0].analysis || "" : "";
    },
  },
  mounted() {
    this.updateViewport();
    window.addEventListener("resize", this.updateViewport);
    this.loadShare();
    this.getWebInfo()
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateViewport);
  },
  methods: {
    ...mapMutations(["setCompany"]),
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
      if (!this.isMobile) this.navOpen = true;
    },
    loadShare() {
      this.shareToken = this.$route.query.share_token || this.$route.query.token || "";
      if (!this.shareToken) {
        this.loading = false;
        return;
      }

      Promise.all([
        this.$axios.get("/app/exam_share/getInfo?token=" + encodeURIComponent(this.shareToken)),
        this.$axios.post("/app/exam_share/partList?token=" + encodeURIComponent(this.shareToken)),
      ])
        .then(([infoRes, listRes]) => {
          if (infoRes.code != 1 || listRes.code != 1) {
            throw new Error((infoRes.msg || listRes.msg || "试卷分享已失效"));
          }
          this.examInfo = infoRes.data || {};
          this.catalog = (listRes.data && (listRes.data.list || listRes.data.catalog)) || [];
          this.flatParts = this.flattenParts(this.catalog);
          this.navOpen = !this.isMobile;
          if (this.flatParts.length) return this.loadQuestion(0);
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.$Message.error(error.message || "试卷加载失败");
        });
    },
    flattenParts(catalog) {
      const result = [];
      (catalog || []).forEach((group) => {
        (group.child || group.children || []).forEach((child) => {
          (child.parts || []).forEach((part) => result.push(part));
        });
      });
      return result;
    },
    selectPart(index) {
      if (index < 0 || index >= this.flatParts.length || index === this.currentIndex) return;
      this.currentIndex = index;
      if (this.isMobile) this.navOpen = false;
      this.loadQuestion(index);
    },
    loadQuestion(index) {
      const part = this.flatParts[index];
      if (!part) return;
      this.loading = true;
      const questionId = part.question_id || (part.question && part.question.id) || part.id;
      const base = "token=" + encodeURIComponent(this.shareToken);
      Promise.all([
        this.$axios.get("/app/exam_share/rightPartKey?question_id=" + questionId + "&" + base),
        this.$axios.get("/app/exam_share/question?question_id=" + questionId + "&" + base),
      ])
        .then(([answerRes, questionRes]) => {
          if (answerRes.code != 1 || questionRes.code != 1) {
            throw new Error(answerRes.msg || questionRes.msg || "题目加载失败");
          }
          this.answerInfo = answerRes.data || [];
          this.currentQuestion = questionRes.data || {};
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.$Message.error(error.message || "题目加载失败");
        });
    },
    letter(index) {
      return String.fromCharCode(65 + index);
    },
    formatAnswer(value, question) {
      if (value === null || value === undefined || value === "") return "未作答";
      if (question && question.question_type && question.question_type.basic_ques_types == 11) return value;
      if (typeof value === "number" && value < 7) return this.letter(value);
      return String(value);
    },
     getWebInfo() {
        this.$axios
          .get("/app/auth/getWebInfo")
          .then((res) => {
            if (res.code == 1) {
              this.getWeb = res.data
              this.setCompany(res.data.company_name);
              window.document.title = res.data.company_name
            } else {
              this.$Message.error(res.msg);
            }
          });
      },
  },
};
</script>

<style scoped lang="less">
.share-page {
  min-height: 100vh;
  background: #f4f6fa;
  color: #333;
  box-sizing: border-box;
}

.share-page *,
.share-page *::before,
.share-page *::after {
  box-sizing: border-box;
}

.share-container {
  width: calc(100% - 48px);
  max-width: 1280px;
  margin: 0 auto;
}

.share-header {
  color: #fff;
  background: linear-gradient(180deg, #002288 0%, #555af3 100%);
}

.share-header-inner {
  padding: 18px 0 20px;
}

.student-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.student-name {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.5;
}

.student-meta,
.exam-time {
  font-size: 14px;
  line-height: 1.7;
}

.student-logo {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
}

.share-main {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
  padding-top: 20px;
  padding-bottom: 104px;
}

.answer-card-panel,
.question-panel {
  min-width: 0;
  background: #fff;
  border: 1px solid #e7ebf3;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(25, 42, 70, 0.04);
}

.answer-card-panel {
  position: sticky;
  top: 16px;
  max-height: calc(100vh - 32px);
  overflow: auto;
}

.panel-title,
.question-heading {
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  border-bottom: 1px solid #edf0f5;
}

.panel-title {
  color: #002288;
  font-size: 16px;
  font-weight: 600;
}

.answer-groups {
  padding: 8px 14px 18px;
}

.answer-group h3 {
  margin: 12px 0 8px;
  color: #333;
  font-size: 14px;
}

.answer-child + .answer-child {
  margin-top: 12px;
}

.child-title {
  margin-bottom: 6px;
  color: #666;
  font-size: 13px;
}

.answer-numbers {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.answer-number,
.card-toggle,
.desktop-card-button,
.question-navigation button {
  border: 0;
  cursor: pointer;
  font-family: inherit;
}

.answer-number {
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid #d7e2eb;
  border-radius: 50%;
  color: #333;
  background: #fafbfc;
}

.answer-number.active {
  border-color: #002288;
  color: #fff;
  background: #002288;
}

.panel-empty,
.state-box {
  padding: 48px 20px;
  color: #8b93a3;
  text-align: center;
}

.question-title {
  min-width: 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.desktop-card-button,
.card-toggle {
  color: #002288;
  background: transparent;
}

.desktop-card-button {
  display: none;
  padding: 4px 0;
}

.answer-table-wrap {
  width: 100%;
  overflow-x: auto;
  padding: 18px 18px 0;
}

.answer-table {
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
}

.answer-table th,
.answer-table td {
  padding: 10px 8px;
  border: 1px solid #e7ebf3;
  text-align: center;
  word-break: break-word;
}

.answer-table th {
  color: #002288;
  background: #edf1ff;
}

.result-mark {
  display: inline-flex;
  width: 18px;
  height: 18px;
  margin-left: 4px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
}

.result-mark.correct {
  background: #3cb5a9;
}

.result-mark.wrong {
  background: #e76f74;
}

.question-detail {
  padding: 18px 24px 24px;
}

.question-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  padding-bottom: 12px;
  color: #666;
  border-bottom: 1px solid #edf0f5;
  font-size: 14px;
  line-height: 1.6;
}

.question-content {
  padding-top: 12px;
  color: #333;
  font-size: 15px;
  line-height: 1.7;
  word-break: break-word;
}

.question-section + .question-section {
  margin-top: 14px;
}

.question-heading-text {
  margin-bottom: 8px;
  font-weight: 600;
}

.question-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 8px 0;
  padding: 10px 12px;
  border: 1px solid #d7e2eb;
  border-radius: 8px;
}

.option-key {
  flex: 0 0 24px;
  color: #002288;
  font-weight: 600;
}

.option-content {
  min-width: 0;
  flex: 1;
}

.analysis-text,
.knowledge-text {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #edf0f5;
  color: #666;
  font-size: 14px;
  line-height: 1.7;
  word-break: break-word;
}

.knowledge-text {
  margin-top: 10px;
  padding-top: 0;
  border-top: 0;
}

.question-navigation {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-top: 1px solid #e7ebf3;
  background: rgba(255, 255, 255, 0.96);
}

.question-navigation span {
  flex: 1;
  color: #666;
  font-size: 13px;
  text-align: center;
}

.question-navigation button {
  min-width: 100px;
  height: 40px;
  padding: 0 14px;
  border-radius: 6px;
  color: #fff;
  background: #002288;
}

.question-navigation .previous-button {
  color: #333;
  background: #ffc233;
}

.question-navigation button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.card-toggle {
  display: none;
}

@media (max-width: 900px) {
  .share-container {
    width: calc(100% - 32px);
  }

  .share-main {
    grid-template-columns: 280px minmax(0, 1fr);
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .share-container {
    width: calc(100% - 24px);
  }

  .share-header-inner {
    padding: 14px 0 16px;
  }

  .student-name {
    font-size: 16px;
  }

  .student-meta,
  .exam-time {
    font-size: 13px;
  }

  .student-logo {
    width: 48px;
    height: 48px;
  }

  .share-main {
    display: block;
    padding-top: 12px;
    padding-bottom: 76px;
  }

  .card-toggle {
    width: 100%;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    padding: 0 14px;
    border-radius: 6px;
    color: #fff;
    background: #002288;
    font-size: 14px;
    text-align: left;
  }

  .toggle-icon {
    font-size: 20px;
    line-height: 1;
  }

  .answer-card-panel {
    position: static;
    max-height: 46vh;
    margin-bottom: 12px;
  }

  .panel-title {
    min-height: 44px;
    font-size: 15px;
  }

  .answer-groups {
    padding: 4px 10px 12px;
  }

  .question-heading {
    min-height: 48px;
    padding: 0 14px;
  }

  .desktop-card-button {
    display: block;
    font-size: 13px;
  }

  .answer-table-wrap {
    padding: 12px 10px 0;
  }

  .answer-table {
    font-size: 12px;
  }

  .answer-table th,
  .answer-table td {
    padding: 8px 6px;
  }

  .question-detail {
    padding: 14px 14px 18px;
  }

  .question-meta {
    display: block;
    font-size: 13px;
  }

  .question-meta span {
    display: inline-block;
    margin: 0 12px 4px 0;
  }

  .question-content {
    font-size: 14px;
  }

  .question-option {
    padding: 8px 10px;
  }

  .question-navigation {
    padding: 10px;
  }

  .question-navigation button {
    min-width: 88px;
    height: 40px;
    padding: 0 10px;
    font-size: 13px;
  }
}
</style>
